import { createFileRoute } from "@tanstack/react-router";
import { MapPin, Clock, Car } from "lucide-react";
import { PageHero } from "@/components/page-hero";
import { Section } from "@/components/section";
import { Reveal } from "@/components/reveal";
import { CtaBand } from "@/components/cta-band";
import { ResponsiveImg } from "@/components/responsive-img";
import { BUSINESS } from "@/lib/site-data";
import { lcpPreload, imgSizes } from "@/lib/images";

export const Route = createFileRoute("/directions")({
  head: () => ({
    meta: [
      { title: "Directions | Magnolia House, 901 Jacobean Court" },
      {
        name: "description",
        content:
          "Magnolia House Custom Picture Framing is at 901 Jacobean Court, Fuquay-Varina, NC 27526. By appointment — call (919) 552-6802 for directions and parking.",
      },
      {
        property: "og:title",
        content: "Directions to Magnolia House Custom Picture Framing",
      },
      {
        property: "og:description",
        content: "901 Jacobean Court, Fuquay-Varina, NC. New location since 2025, by appointment.",
      },
    ],
    links: [lcpPreload("shop-exterior")],
  }),
  component: DirectionsPage,
});

const DETAILS = [
  {
    icon: MapPin,
    title: BUSINESS.address,
    body: "In Fuquay-Varina, Wake County. Since early 2025, after 24 years at 218 South Main Street.",
  },
  {
    icon: Clock,
    title: "By appointment",
    body: "Available most days and times, including after hours. Please don't hesitate to call.",
  },
  {
    icon: Car,
    title: "Easy parking",
    body: "Park right at the door and carry your piece straight in — much easier than the old Main Street shop.",
  },
];

function DirectionsPage() {
  return (
    <>
      <PageHero
        eyebrow="Directions"
        title={
          <>
            We've moved —
            <br />
            <span className="amber-text italic">here's where.</span>
          </>
        }
        lead="901 Jacobean Court, Fuquay-Varina, NC 27526."
        image="shop-exterior"
        alt="Magnolia House shop exterior"
      />

      <Section>
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <div>
            <ul className="space-y-8">
              {DETAILS.map((d, i) => (
                <Reveal as="li" key={d.title} delay={i * 90} className="flex gap-4">
                  <d.icon className="mt-1 size-4 shrink-0 text-[var(--terracotta)]" aria-hidden />
                  <div>
                    <p className="text-base">{d.title}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{d.body}</p>
                  </div>
                </Reveal>
              ))}
            </ul>
            <Reveal delay={200} className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={BUSINESS.mapsHref}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Open in Google Maps
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
              <a
                href={BUSINESS.phoneHref}
                className="link-underline text-[11px] uppercase tracking-[0.24em] text-muted-foreground hover:text-foreground"
              >
                Call {BUSINESS.phone}
              </a>
            </Reveal>
          </div>

          <Reveal delay={120}>
            <div className="plate">
              <ResponsiveImg
                name="shop-street"
                sizes={imgSizes.half}
                alt="The street outside Magnolia House Custom Picture Framing"
                loading="lazy"
                decoding="async"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </Section>

      <Section tone="mist">
        <Reveal>
          <div className="overflow-hidden rounded-xl shadow-[var(--shadow-soft)]">
            <iframe
              title="Map to Magnolia House Custom Picture Framing"
              src="https://www.google.com/maps?q=901%20Jacobean%20Court%2C%20Fuquay-Varina%2C%20NC%2027526&output=embed"
              className="h-[60vh] w-full grayscale-[0.35] transition-[filter] duration-700 hover:grayscale-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>
      </Section>

      <CtaBand />
    </>
  );
}
